import { useCallback, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Logo from '@/assets/logo.png';
import {
  Button,
  Heading,
  Paragraph,
  Spinner,
  StyledLink
} from '@/components/atoms';
import { TextInput } from '@/components/atoms/TextInput';
import { AuthForm } from '@/components/molecules';
import { useIntl } from '@/intl';
import { login } from '@/services/auth.service';
import environmentService from '@/services/environment.service';

const buildRedirectPayload = (returnTo: string | null): string | undefined => {
  if (!returnTo) return undefined;

  try {
    const appUrl = new URL(environmentService.appUrl);
    const targetUrl = new URL(returnTo);

    if (targetUrl.origin !== appUrl.origin) return undefined;

    return window.btoa(
      JSON.stringify({
        origin: appUrl.origin,
        target_url: targetUrl.toString()
      })
    );
  } catch {
    return undefined;
  }
};

const signupConfirmationMessages = {
  confirmed: {
    title: 'signup.confirmation.confirmed.title',
    message: 'signup.confirmation.confirmed.message'
  },
  'already-confirmed': {
    title: 'signup.confirmation.alreadyConfirmed.title',
    message: 'signup.confirmation.alreadyConfirmed.message'
  },
  expired: {
    title: 'signup.confirmation.expired.title',
    message: 'signup.confirmation.expired.message'
  },
  invalid: {
    title: 'signup.confirmation.invalid.title',
    message: 'signup.confirmation.invalid.message'
  },
  unavailable: {
    title: 'signup.confirmation.unavailable.title',
    message: 'signup.confirmation.unavailable.message'
  }
} as const;

const getSignupConfirmationMessage = (status: string | null) => {
  if (
    !status ||
    !Object.prototype.hasOwnProperty.call(signupConfirmationMessages, status)
  )
    return undefined;

  return signupConfirmationMessages[
    status as keyof typeof signupConfirmationMessages
  ];
};

const Login: React.FC = () => {
  const { formatMessage } = useIntl();
  const [searchParams] = useSearchParams();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();

  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string>();

  const queryError = searchParams.get('error');
  const redirect = buildRedirectPayload(searchParams.get('return_to'));
  const signupConfirmationMessage = getSignupConfirmationMessage(
    searchParams.get('confirmation')
  );

  const handleLogin = useCallback(async () => {
    try {
      let recaptchaToken = '';
      const requiresRecaptcha = environmentService.recaptchaKey.length > 0;

      if (requiresRecaptcha) {
        if (!executeRecaptcha) throw new Error('Recaptcha not loaded');

        recaptchaToken = await executeRecaptcha('login');
        if (!recaptchaToken) throw new Error('Recaptcha token not found');
      }

      setLoading(true);
      await login(email.toLowerCase(), recaptchaToken, redirect);
      setSuccess(true);
    } catch (e: any) {
      if (e?.message === 'user not found') {
        setError(formatMessage({ id: 'login.errors.userNotFound' }));
      } else if (e?.message === 'magic link login disabled') {
        setError(formatMessage({ id: 'login.errors.magicLinkLoginDisabled' }));
      } else {
        setError(formatMessage({ id: 'shared.errors.generic' }));
      }
    } finally {
      setLoading(false);
    }
  }, [email, executeRecaptcha, redirect]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (email.length === 0) {
      setError(formatMessage({ id: 'shared.errors.invalidEmail' }));
      return;
    }
    void handleLogin();
  };

  const handleSsoLogin = () => {
    navigate('/sso', { state: { email } });
  };

  const signupConfirmation = searchParams.get('mode') === 'signup';

  return (
    <AuthForm
      onSubmit={handleSubmit}
      shellTopLeft={
        <img src={Logo} alt='Vamoz' className='h-6 w-auto sm:h-10' />
      }>
      <div className='mx-auto w-full max-w-[28rem] -translate-y-8 space-y-6 sm:max-w-[30rem] sm:translate-y-0 sm:space-y-10'>
        <div className='space-y-4 text-center'>
          <Heading
            type='h1'
            className='mx-auto max-w-lg text-3xl font-semibold leading-[1.05] tracking-tight text-black sm:text-5xl'>
            {success
              ? formatMessage({ id: 'login.title.checkEmail' })
              : signupConfirmationMessage
              ? formatMessage({ id: signupConfirmationMessage.title })
              : signupConfirmation
              ? formatMessage({ id: 'login.title.emailConfirmed' })
              : formatMessage({ id: 'login.title.welcome' })}
          </Heading>

          {signupConfirmationMessage ? (
            <Paragraph className='text-black/65 mx-auto max-w-xl px-3 text-sm leading-6 sm:px-0 sm:text-base sm:leading-7'>
              {formatMessage({ id: signupConfirmationMessage.message })}
            </Paragraph>
          ) : signupConfirmation ? (
            <Paragraph className='text-black/65 mx-auto max-w-xl px-3 text-sm leading-6 sm:px-0 sm:text-base sm:leading-7'>
              {formatMessage({ id: 'login.subtitle.emailConfirmed' })}
            </Paragraph>
          ) : !success ? (
            <Paragraph className='text-black/65 mx-auto max-w-xl px-3 text-sm leading-6 sm:px-0 sm:text-base sm:leading-7'>
              {formatMessage({ id: 'login.subtitle.enterEmail' })}
            </Paragraph>
          ) : null}
        </div>

        {queryError === 'session-expired' && !success && (
          <Paragraph className='rounded-2xl border border-red/20 bg-red/10 px-4 py-3 text-sm leading-6 !text-red'>
            {formatMessage({ id: 'login.errors.sessionExpired' })}
          </Paragraph>
        )}

        {loading ? (
          <div className='bg-white/92 flex justify-center rounded-[2rem] border border-black/5 px-5 py-8 backdrop-blur sm:px-6 sm:py-12'>
            <Spinner />
          </div>
        ) : success ? (
          <div className='rounded-[2rem] border border-emerald-200 bg-emerald-50/80 p-7'>
            <Paragraph className='leading-7 text-emerald-950'>
              {formatMessage({ id: 'login.subtitle.emailSent' })}
            </Paragraph>
          </div>
        ) : (
          <>
            <div className='bg-white/92 mx-auto w-full rounded-[2rem] border border-black/5 px-4 pb-4 pt-4 backdrop-blur sm:px-7 sm:pb-6 sm:pt-7'>
              <div className='space-y-4 sm:space-y-5'>
                <TextInput
                  type='email'
                  id='email'
                  name='email'
                  placeholder={formatMessage({
                    id: 'shared.placeholders.companyEmail'
                  })}
                  value={email}
                  error={!!error}
                  className='h-12 text-sm sm:h-14 sm:text-base'
                  onChange={e => {
                    setEmail(e.target.value);
                    if (error) setError(undefined);
                  }}
                />

                {error && (
                  <Paragraph className='rounded-2xl border border-red/20 bg-red/10 px-4 py-3 text-sm leading-6 !text-red'>
                    {error}
                  </Paragraph>
                )}

                <Button
                  type='submit'
                  variant='primary'
                  className='h-11 w-full rounded-2xl px-5 text-sm font-medium sm:h-12 sm:text-base'>
                  {formatMessage({ id: 'login.button' })}
                </Button>

                <Button
                  type='button'
                  variant='gray'
                  className='h-11 w-full rounded-2xl px-5 text-sm font-medium sm:h-12 sm:text-base'
                  onClick={handleSsoLogin}>
                  {formatMessage({ id: 'login.ssoButton' }) || 'Login via SSO'}
                </Button>
              </div>

              <div className='mt-4 border-t border-black/10 pt-3 sm:mt-5 sm:pt-4'>
                <Paragraph className='text-xs leading-5 text-black/60 sm:text-sm sm:leading-6'>
                  {formatMessage({ id: 'login.dontHaveAccount' })}{' '}
                  <StyledLink
                    href='mailto:support@vamoz.io'
                    variant='highlighted'
                    className='font-medium'>
                    {formatMessage({ id: 'login.conactUs' })}
                  </StyledLink>
                </Paragraph>
              </div>
            </div>
          </>
        )}
      </div>
    </AuthForm>
  );
};

export default Login;
